import { useEffect, useState } from "react";
import {
  Search,
  Mail,
  ArrowRight,
  Plus,
  Trash2,
  Inbox,
} from "lucide-react";
import {
  Button,
  Card,
  Input,
  Pill,
  Avatar,
  EmptyState,
  Dialog,
  toast,
} from "../index.js";
import "./DesignDocs.css";

// Tokens to surface as swatches. Grouped for readability.
const TOKEN_GROUPS = [
  {
    label: "Surfaces",
    tokens: ["--background", "--background-muted", "--background-hero", "--card", "--muted", "--secondary"],
  },
  {
    label: "Text",
    tokens: ["--foreground", "--foreground-muted", "--card-foreground", "--muted-foreground", "--secondary-foreground"],
  },
  {
    label: "Brand — gold",
    tokens: ["--primary", "--primary-foreground", "--primary-hover", "--primary-pressed", "--primary-dark"],
  },
  {
    label: "Brand — forest green",
    tokens: ["--accent", "--accent-foreground", "--accent-light", "--accent-dark", "--accent-hover", "--accent-pressed"],
  },
  {
    label: "On-dark",
    tokens: ["--on-dark", "--on-dark-muted", "--on-dark-subtle", "--on-dark-bubble", "--on-dark-bubble-strong"],
  },
  {
    label: "Lines + inputs",
    tokens: ["--border", "--border-strong", "--input", "--ring"],
  },
  {
    label: "Status",
    tokens: [
      "--success", "--success-foreground", "--success-soft",
      "--warning", "--warning-foreground", "--warning-soft",
      "--info", "--info-foreground", "--info-soft",
      "--destructive", "--destructive-foreground", "--destructive-soft",
      "--destructive-hover", "--destructive-pressed",
    ],
  },
  {
    label: "Gradients",
    tokens: ["--gradient-header-forest", "--gradient-hero-dual-radial"],
  },
];

const SPACING = ["--space-1", "--space-2", "--space-3", "--space-4", "--space-5", "--space-6", "--space-8", "--space-10", "--space-12", "--space-16"];
const RADII = ["--radius-sm", "--radius-md", "--radius-lg", "--radius-xl", "--radius-full"];
const SHADOWS = ["--shadow-sm", "--shadow-md", "--shadow-lg"];
const TYPE = [
  ["--font-size-xs", "Extra small"],
  ["--font-size-sm", "Small"],
  ["--font-size-base", "Base"],
  ["--font-size-lg", "Large"],
  ["--font-size-xl", "Extra large"],
  ["--font-size-2xl", "2X large"],
  ["--font-size-3xl", "3X large"],
];

function useTokenValues(tokens) {
  const [values, setValues] = useState({});
  useEffect(() => {
    const styles = getComputedStyle(document.documentElement);
    const out = {};
    tokens.forEach((t) => {
      out[t] = styles.getPropertyValue(t).trim();
    });
    setValues(out);
  }, [tokens]);
  return values;
}

function Swatch({ token, value }) {
  const isGradient = value && value.includes("gradient");
  return (
    <div className="vsdd-swatch">
      <div
        className="vsdd-swatch__chip"
        style={{ background: `var(${token})` }}
        aria-hidden="true"
      />
      <div className="vsdd-swatch__meta">
        <code className="vsdd-swatch__name">{token}</code>
        <code className="vsdd-swatch__value">
          {isGradient ? "(gradient)" : value || "—"}
        </code>
      </div>
    </div>
  );
}

const BUTTON_VARIANTS = ["primary", "secondary", "ghost", "destructive"];
const BUTTON_SIZES = ["sm", "md", "lg"];
const PILL_VARIANTS = [
  "p0", "p1", "p2",
  "open", "in-progress", "done",
  "success", "warning", "danger", "neutral",
];

export default function DesignDocs() {
  const allTokens = TOKEN_GROUPS.flatMap((g) => g.tokens);
  const colorValues = useTokenValues(allTokens);
  const spacingValues = useTokenValues(SPACING);
  const radiusValues = useTokenValues(RADII);
  const shadowValues = useTokenValues(SHADOWS);
  const typeValues = useTokenValues(TYPE.map((t) => t[0]));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [demoText, setDemoText] = useState("");

  return (
    <div className="vsdd">
      <header className="vsdd__page-head">
        <h1>VillaHub design system</h1>
        <p>
          Visual reference for the primitive components in <code>frontend/src/ui/</code>.
          Every value below is driven by a CSS variable in <code>frontend/src/styles/tokens.css</code>.
        </p>
      </header>

      <section className="vsdd__section">
        <h2>Color tokens</h2>
        {TOKEN_GROUPS.map((group) => (
          <div className="vsdd__group" key={group.label}>
            <h3>{group.label}</h3>
            <div className="vsdd__swatch-grid">
              {group.tokens.map((t) => (
                <Swatch key={t} token={t} value={colorValues[t]} />
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="vsdd__section">
        <h2>Typography</h2>
        <div className="vsdd__type-list">
          {TYPE.map(([token, label]) => (
            <div key={token} className="vsdd__type-row">
              <div className="vsdd__type-sample" style={{ fontSize: `var(${token})` }}>
                The quick brown fox jumps
              </div>
              <code className="vsdd__type-meta">
                {token} · {typeValues[token] || "—"} · {label}
              </code>
            </div>
          ))}
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Spacing</h2>
        <div className="vsdd__spacing-list">
          {SPACING.map((s) => (
            <div key={s} className="vsdd__spacing-row">
              <div className="vsdd__spacing-bar" style={{ width: `var(${s})` }} />
              <code>
                {s} · {spacingValues[s] || "—"}
              </code>
            </div>
          ))}
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Radii</h2>
        <div className="vsdd__radius-grid">
          {RADII.map((r) => (
            <div key={r} className="vsdd__radius-tile">
              <div
                className="vsdd__radius-shape"
                style={{ borderRadius: `var(${r})` }}
              />
              <code>
                {r} · {radiusValues[r] || "—"}
              </code>
            </div>
          ))}
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Shadows</h2>
        <div className="vsdd__shadow-grid">
          {SHADOWS.map((s) => (
            <div key={s} className="vsdd__shadow-tile">
              <div
                className="vsdd__shadow-shape"
                style={{ boxShadow: `var(${s})` }}
              />
              <code>
                {s} · {shadowValues[s] || "—"}
              </code>
            </div>
          ))}
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Buttons</h2>
        <p className="vsdd__muted">Every variant × every size (4 × 3).</p>
        <div className="vsdd__button-grid">
          {BUTTON_VARIANTS.map((v) => (
            <div key={v} className="vsdd__button-row">
              <div className="vsdd__label-tag">{v}</div>
              {BUTTON_SIZES.map((sz) => (
                <Button key={sz} variant={v} size={sz}>
                  {v} {sz}
                </Button>
              ))}
            </div>
          ))}
        </div>
        <div className="vsdd__button-extras">
          <Button leftIcon={<Plus size={16} />}>Left icon</Button>
          <Button rightIcon={<ArrowRight size={16} />}>Right icon</Button>
          <Button loading>Loading</Button>
          <Button disabled>Disabled</Button>
          <Button variant="destructive" leftIcon={<Trash2 size={16} />}>
            Delete
          </Button>
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Pills</h2>
        <div className="vsdd__pill-row">
          {PILL_VARIANTS.map((p) => (
            <Pill key={p} variant={p}>
              {p}
            </Pill>
          ))}
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Cards</h2>
        <Card>
          <p>A bare card. No header, just body content sitting on the default padding.</p>
        </Card>
        <div className="vsdd__spacer" />
        <Card
          title="With title and right action"
          subtitle="Subtitle text gives extra context to the user."
          rightAction={<Button size="sm" variant="ghost">More</Button>}
        >
          <p>
            Card bodies stack vertically with token spacing. Anything you pass as children
            becomes part of the body.
          </p>
        </Card>
        <div className="vsdd__spacer" />
        <div className="vsdd__cards-grid">
          <Card title="Left column" subtitle="Two-column grid">
            <p>Cards drop into any layout grid.</p>
          </Card>
          <Card title="Right column" subtitle="Two-column grid">
            <p>The wrapper grid controls width; the card just renders.</p>
          </Card>
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Inputs</h2>
        <div className="vsdd__inputs-grid">
          <Input
            label="Default"
            placeholder="Type something"
            value={demoText}
            onChange={(e) => setDemoText(e.target.value)}
          />
          <Input
            label="Focused (click me)"
            placeholder="Focus to see ring"
            autoFocus={false}
          />
          <Input
            label="With error"
            placeholder="invalid@example"
            defaultValue="not-an-email"
            error="That doesn't look like a valid email."
          />
          <Input
            label="With helper"
            placeholder="Anything"
            helper="We'll never share this with anyone else."
          />
          <Input
            label="With left icon"
            placeholder="Search…"
            leftIcon={<Search size={18} />}
          />
          <Input
            label="Secure"
            placeholder="Password"
            secure
          />
          <Input
            label="With email icon"
            placeholder="you@example.com"
            leftIcon={<Mail size={18} />}
            type="email"
          />
          <Input
            label="Disabled"
            placeholder="Read-only"
            disabled
            defaultValue="Cannot edit"
          />
        </div>
      </section>

      <section className="vsdd__section">
        <h2>Avatars</h2>
        <div className="vsdd__avatar-row">
          {[32, 40, 48, 64].map((sz) => (
            <Avatar
              key={`img-${sz}`}
              size={sz}
              name="Aarav Sharma"
              imageUrl={`https://i.pravatar.cc/${sz * 2}?img=${sz}`}
            />
          ))}
          {[32, 40, 48, 64].map((sz) => (
            <Avatar key={`init-${sz}`} size={sz} name={`User ${sz}`} />
          ))}
        </div>
      </section>

      <section className="vsdd__section">
        <h2>EmptyState</h2>
        <EmptyState
          icon={<Inbox size={28} strokeWidth={1.6} />}
          title="No notifications yet"
          subtitle="When something needs your attention, it'll show up here."
          cta={
            <Button leftIcon={<Plus size={16} />}>Create one</Button>
          }
        />
      </section>

      <section className="vsdd__section">
        <h2>Toast + Dialog</h2>
        <div className="vsdd__interactive-row">
          <Button onClick={() => toast.success("Saved successfully")}>
            Show success toast
          </Button>
          <Button variant="secondary" onClick={() => toast.warning("Heads up — check this")}>
            Show warning toast
          </Button>
          <Button variant="destructive" onClick={() => toast.danger("Something broke")}>
            Show danger toast
          </Button>
          <Button variant="ghost" onClick={() => toast.info("Just so you know…")}>
            Show info toast
          </Button>
          <Button onClick={() => setDialogOpen(true)}>Open dialog</Button>
        </div>
      </section>

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Example dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setDialogOpen(false);
                toast.success("Confirmed");
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p>
          Press <kbd>Esc</kbd> or click the backdrop to close. Tab cycles focus
          inside the dialog.
        </p>
        <div className="vsdd__spacer" />
        <Input label="Try focusing here" placeholder="Type…" />
      </Dialog>
    </div>
  );
}
